// Main column + right-column modules. At wide widths the modules stack in a
// fixed-width column to the right of the body. Below the breakpoint the page
// picks one of two narrow layouts (`Aside.narrow`):
//
//   strip — the modules become a swipeable row of summary cards ABOVE the
//           body (see AsideModule). For pages whose body is long or endless
//           (the home feed, a group's feed): anything after it is
//           unreachable. While the strip is live the aside also comes first
//           in the DOM, so keyboard and screen-reader users reach it before
//           the body; in the column and the stack the body comes first, as
//           it reads.
//   stack — the full modules follow the body. For pages whose body is
//           finite and the modules are secondary to it (an article, a form).
//
// Which layout is live is measured here (in rem, so the breakpoint tracks
// the text size) and published through AsideLayoutContext: modules render a
// different shape in the strip, which CSS alone can't choose.
//
// The column also needs the shell out of its phone (`sm`) layout. The shell's
// phone layout narrows the page gutters, which widens the content by more
// than a column's breakpoint margin: measured on width alone, narrowing the
// window across the shell's breakpoint would flip a strip back to a column,
// then to a strip again a few pixels later.
//
// Contract: every direct child of `modules` is one module, so callers pass a
// fragment, not their own wrapping Stack.

import { Fragment, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Stack, type SpaceKey } from '@leanin/ui';

import { useShellBreakpoint } from '../shell/AppShell';
import { AsideLayoutContext, type AsideLayout } from './aside-layout';
import styles from './BodyWithAside.module.css';
import { useStripFills } from './use-strip-fills';

export type Aside = {
  /** The modules, as a fragment — one direct child per module. */
  modules: ReactNode;
  /** Pin the column while the body scrolls (column layout only). */
  sticky?: boolean;
} & (
  /** The layout below the side-by-side breakpoint. A strip's column is the
   *  320 tier: its summary cards and their breakpoint are sized against it. */
  | { narrow: 'strip' }
  | {
      narrow: 'stack';
      /** Column width tier — 320 (default) or 380 for the wider editorial
       *  sidecar used by create forms. */
      width?: 320 | 380;
    }
);

export type BodyWithAsideProps = {
  /** Main column content. */
  children: ReactNode;
  aside: Aside;
  /** Space above the column, so its first module lines up with the body's
   *  first content row (e.g. below a sticky tab bar's padding); the strip
   *  keeps it above itself. Not applied to a stack, which follows the body. */
  asideOffset?: SpaceKey;
};

// The narrowest container that fits a 320px body beside the column, plus the
// 24px gap and 16px of breathing room, per column tier.
const COLUMN_MIN_WIDTH_REM: Record<320 | 380, number> = { 320: 41.25, 380: 45.25 };

export function BodyWithAside({ children, aside, asideOffset = 'none' }: BodyWithAsideProps) {
  const { modules, narrow, sticky = false } = aside;
  const width = aside.narrow === 'stack' ? (aside.width ?? 320) : 320;
  const shellIsPhone = useShellBreakpoint() === 'sm';
  const containerRef = useRef<HTMLDivElement>(null);
  const modulesRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<AsideLayout | null>(null);
  const stripFills = useStripFills(modulesRef, layout === 'strip');

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const widthRem = container.getBoundingClientRect().width / rootFontPx;
      setLayout(!shellIsPhone && widthRem >= COLUMN_MIN_WIDTH_REM[width] ? 'column' : narrow);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [narrow, width, shellIsPhone]);

  const asideClass = sticky && layout === 'column' ? `${styles['aside']} ${styles['sticky']}` : styles['aside'];
  // Until the first measurement (before paint) the modules don't render, so
  // none of them mounts in a shape it is about to leave.
  const asideRegion = (
    <aside className={asideClass}>
      {layout !== null ? (
        <AsideLayoutContext.Provider value={layout}>
          <div
            ref={modulesRef}
            className={styles['modules']}
            data-fill={layout === 'strip' && stripFills ? '' : undefined}
          >
            {modules}
          </div>
        </AsideLayoutContext.Provider>
      ) : null}
    </aside>
  );
  const body = (
    <Stack gap="none" className={styles['body']}>
      {children}
    </Stack>
  );

  return (
    <div ref={containerRef}>
      <div
        className={styles['layout']}
        data-layout={layout ?? narrow}
        style={{
          '--aside-offset': `var(--space-${asideOffset})`,
          '--aside-width': width === 380 ? '23.75rem' : '20rem',
        } as CSSProperties}
      >
        {/* Keyed, so crossing the breakpoint moves the regions rather than
            remounting the body (and losing a feed's state). */}
        {layout === 'strip'
          ? [<Fragment key="aside">{asideRegion}</Fragment>, <Fragment key="body">{body}</Fragment>]
          : [<Fragment key="body">{body}</Fragment>, <Fragment key="aside">{asideRegion}</Fragment>]}
      </div>
    </div>
  );
}
